import React, { ChangeEvent, useCallback } from "react";
import { MiAutocomplete, MiInputSelectBox } from "@miplatform/components";
import { PaymentType } from "@miplatform/types";
import { Box, Divider, Typography } from "@mui/material";

export type optionsType = { label: string; value: string };

export type Filters = {
  paymentStatus?: string;
  paymentType?: PaymentType;
  groupBy: string;
  accountId: string;
  bankAccountQuickBooksId?: string;
};

const paymentStatusOptions: optionsType[] = [
  { label: "Fully Applied", value: "FullyPaid" },
  { label: "Not Fully Applied", value: "PartialPaid" },
];

const paymentTypeOptions: optionsType[] = [
  { label: "Other", value: PaymentType.Other },
  { label: "Credit Card", value: PaymentType.CreditCard },
  { label: "Check", value: PaymentType.Check },
  { label: "Cash", value: PaymentType.Cash },
];

type FilterComponentProps = {
  customerOptions: optionsType[];
  bankAccountsOptions: optionsType[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
};

export const PaymentListFilterComponent: React.FC<FilterComponentProps> = ({
  customerOptions,
  bankAccountsOptions,
  filters,
  setFilters,
}) => {
  const handlePaymentStatusChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        paymentStatus:
          event.target.value === "None" ? undefined : event.target.value,
      }));
    },
    [setFilters]
  );

  const handlePaymentTypeChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        paymentType:
          event.target.value === "None"
            ? undefined
            : (event.target.value as PaymentType),
      }));
    },
    [setFilters]
  );

  const handleCustomerChange = useCallback(
    (_: any, value: optionsType | null) => {
      setFilters((prev) => ({
        ...prev,
        accountId: value?.value || "",
      }));
    },
    [setFilters]
  );

  const handleBankAccountChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        bankAccountQuickBooksId:
          event.target.value === "None" ? undefined : event.target.value,
      }));
    },
    [setFilters]
  );

  return (
    <Box height="100%" flex={0.2}>
      <Typography variant="body2" fontWeight={600}>
        Filter & Sort
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box>
        <Typography fontSize={12} fontWeight={600} my={2}>
          Payment Status
        </Typography>
        <MiInputSelectBox
          options={paymentStatusOptions}
          value={filters.paymentStatus}
          handleChange={(event: ChangeEvent<HTMLSelectElement>) =>
            handlePaymentStatusChange(event)
          }
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Customer
        </Typography>
        <MiAutocomplete
          name="customerOptions"
          getOptionLabel={(option: optionsType) => option.label}
          options={customerOptions}
          value={
            customerOptions.find((opt) => opt.value === filters.accountId) ||
            null
          }
          renderOption={(props, option) => (
            <li {...props} key={option.value}>
              {option.label}
            </li>
          )}
          required
          onChange={handleCustomerChange}
        />

        <Typography fontSize={12} fontWeight={600} my={2}>
          Payment Type
        </Typography>
        <MiInputSelectBox
          options={paymentTypeOptions}
          value={filters.paymentType}
          handleChange={handlePaymentTypeChange}
        />

        <Typography fontSize={12} fontWeight={600} my={2}>
          Bank Account
        </Typography>
        <MiInputSelectBox
          options={bankAccountsOptions}
          value={filters.bankAccountQuickBooksId}
          handleChange={(event: ChangeEvent<HTMLSelectElement>) =>
            handleBankAccountChange(event)
          }
        />
      </Box>
    </Box>
  );
};
